import React from "react";

import Iconify from "@/src/components/iconify";
import IconCode from "@/src/lib/iconCode";
import Image from "next/image";
import Label from "@/src/components/common/Label";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";
import CustomChip from "@/src/lib/ui/customChip";
import JobApplicationForm from "@/src/modules/careers/jobApplicationForm";

export default function JobDetail({ job }) {
    const formRef = React.useRef(null);

    const {
        jobId,
        jobTitle,
        locationName,
        jobSummary,
        jobDetail,
        linkedInUrl,
        naukriUrl,
        contactEmail,
        allowWhatsapp,
        contactWhatsapp,
        educationTags,
        skillTags,
        experienceTags,
        locationUrl,
    } = job?.data || {};

    const handleApplyNow = () => {
        if (formRef.current) {
            const elementPosition = formRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 120; // 120px offset above

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    const handleWhatsappClick = (event) => {
        event.stopPropagation();
        if (contactWhatsapp) {
            window.open(`https://wa.me/${contactWhatsapp}`, "_blank");
        }
    };

    const handleLocationClick = () => {
        if (locationUrl) {
            window.open(locationUrl, "_blank");
        }
    };

    const openNewTab = (url) => {
        window.open(url.startsWith("http") ? url : `https://${url}`, "_blank");
    };

    return (
        <>
            {/* Banner Section */}
            <div
                className="relative w-full h-[200px] md:h-[300px] md:mb-6 mb-2 object-cover object-center md:pt-20 pt-10"
                style={{
                    backgroundImage: `url('/images/3.jpg')`,
                }}
            >
                {/* Overlay */}
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/50" />

                {/* Content */}
                <div className="md:p-4 p-2 bg-white/50 relative z-10 md:mx-12 mx-2 rounded-md w-full max-w-sm">
                    {/* Row: Location label + icon + Apply button */}
                    <div className="md:mb-2 mb-0 flex flex-row items-center justify-between">
                        {/* Left: Job Title */}
                        <h4 className="text-xl font-bold text-black">
                            {jobTitle || "Job Title Not Available"}
                        </h4>

                        {/* Right: Location Label + Icon */}
                        <div className="flex flex-row items-center gap-1">
                            <Label color="default" className="cursor-pointer text-[1.1rem] font-medium">
                                {locationName || "Location Not Specified"}
                            </Label>
                        </div>
                    </div>

                    <div className="flex flex-row items-center justify-end">
                        <CustomButton
                            variant="contained"
                            color="error"
                            onClick={handleApplyNow}
                            className="text-white"
                        >
                            Apply now »
                        </CustomButton>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column (60%) */}
                <div className="lg:col-span-7">
                    <div className="shadow-sm rounded-md">
                        <div className="md:p-4 p-2">
                            {/* Header: Job Title & Location */}
                            <div
                                className="flex flex-row items-center justify-between bg-gray-200 p-4 rounded-md mb-3 cursor-pointer"
                                onClick={handleLocationClick}
                            >
                                <div className="flex flex-row items-center gap-2">
                                    <Image
                                        src="/images/centre.png"
                                        alt="centre"
                                        width={40}
                                        height={40}
                                    />
                                    <div>
                                        <h6 className="font-bold md:text-lg text-base">
                                            {jobTitle}
                                        </h6>

                                        <p>{locationName}</p>
                                    </div>
                                </div>
                                <CustomIconButton>
                                    <Iconify
                                        icon={IconCode.googleMapPin}
                                        width={32}
                                        height={32}
                                    />
                                </CustomIconButton>
                            </div>
                            {/* Job Summary */}
                            {jobSummary && (
                                <>
                                    <h6 className="font-bold md:text-lg text-sm">
                                        Job Summary
                                    </h6>

                                    <p className="mb-3">{jobSummary}</p>

                                    <div className="md:my-5 my-2 border-b border-gray-300 border-dashed"></div>
                                </>
                            )}
                            {/* Job Description */}
                            {jobDetail && (
                                <>
                                    <h6 className="font-bold md:text-lg text-sm">
                                        Job Description
                                    </h6>

                                    <div
                                        className="prose prose-sm md:prose-lg xl:prose-xl text-black max-w-none mb-3 [&>p]:my-1"
                                        dangerouslySetInnerHTML={{
                                            __html: jobDetail,
                                        }}
                                    />
                                    <div className="md:my-6 my-2 border-b border-gray-300 border-dashed"></div>
                                </>
                            )}

                            <div className="flex flex-col sm:flex-row gap-2">
                                {/* Apply via WhatsApp */}
                                {allowWhatsapp === "1" && contactWhatsapp && (
                                    <CustomButton
                                        variant="contained"
                                        onClick={handleWhatsappClick}
                                        className="capitalize text-white rounded-xs"
                                        style={{
                                            backgroundColor: "#25D366",
                                        }}
                                        startIcon={
                                            <Iconify
                                                icon={IconCode.whatsapp}
                                                width={20}
                                            />
                                        }
                                    >
                                        Chat on WhatsApp
                                    </CustomButton>
                                )}

                                {/* Apply via LinkedIn */}
                                {linkedInUrl && (
                                    <CustomButton
                                        variant="contained"
                                        onClick={() => openNewTab(linkedInUrl)}
                                        className="capitalize text-white rounded-xs"
                                        style={{
                                            backgroundColor: "#0072B1",
                                        }}
                                        startIcon={
                                            <Iconify
                                                icon={IconCode.linkedIn}
                                                width={20}
                                                color="#fff"
                                            />
                                        }
                                    >
                                        Apply With LinkedIn
                                    </CustomButton>
                                )}

                                {/* Apply via Naukri */}
                                {naukriUrl && (
                                    <CustomButton
                                        variant="contained"
                                        onClick={() => openNewTab(naukriUrl)}
                                        className="capitalize rounded-xs bg-slate-100"
                                        style={{
                                            color: "#265df5",
                                        }}
                                        startIcon={
                                            <img
                                                src="/images/naukri_logo.png"
                                                alt="Naukri Logo"
                                                style={{
                                                    width: 25,
                                                    height: 25,
                                                }}
                                            />
                                        }
                                    >
                                        Apply via Naukri
                                    </CustomButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column (40%) */}
                <div className="lg:col-span-5">
                    <div className="shadow-sm rounded-md mb-2">
                        <div className="md:p-4 p-2">
                            <h6 className="font-bold mb-1">Requirements</h6>

                            {/* Education */}
                            {educationTags?.length > 0 && (
                                <div className="mb-3">
                                    <p className="mb-0.5 font-medium text-sm">
                                        Education Required:
                                    </p>
                                    <div className="flex flex-row gap-1 flex-wrap">
                                        {educationTags.map((tag, idx) => (
                                            <CustomChip
                                                key={idx}
                                                label={tag}
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Skills */}
                            {skillTags?.length > 0 && (
                                <div className="mb-3">
                                    <p className="mb-0.5 font-medium text-sm">
                                        Skills:
                                    </p>
                                    <div className="flex flex-row gap-1 flex-wrap">
                                        {skillTags.map((tag, idx) => (
                                            <CustomChip
                                                key={idx}
                                                label={tag}
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Experience */}
                            {experienceTags?.length > 0 && (
                                <div>
                                    <p className="mb-0.5 font-medium text-sm">
                                        Experience:
                                    </p>
                                    <div className="flex flex-row gap-1 flex-wrap">
                                        {experienceTags.map((tag, idx) => (
                                            <CustomChip
                                                key={idx}
                                                label={tag}
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="md:my-5 my-2 border-b border-gray-300 border-dashed"></div>

                            <div className="flex justify-end">
                                <CustomButton
                                    variant="contained"
                                    color="error"
                                    onClick={handleApplyNow}
                                    className="capitalize w-fit"
                                >
                                    Apply Now
                                </CustomButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Job Application Form Section */}
            <div className="mt-8" ref={formRef}>
                <JobApplicationForm jobTitle={jobTitle} jobId={jobId} />
            </div>
        </>
    );
}
